import React, { useState, useEffect, useCallback, useRef } from "react";
import { api, fmtNumber } from "@/lib/api";
import { TID } from "@/constants/tids";
import { logError } from "@/lib/logger";
import { useNodeExport } from "@/hooks/useNodeExport";
import TabReportPrint from "@/components/shared/TabReportPrint";
import TabLoadError from "@/components/shared/TabLoadError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { HeartPulse, Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const REMARK_PLUGINS = [remarkGfm];

function sevBadge(sev) {
  if (sev === "high") return <Badge className="bg-red-600 hover:bg-red-600">High</Badge>;
  if (sev === "medium") return <Badge className="bg-amber-500 hover:bg-amber-500">Medium</Badge>;
  return <Badge variant="secondary">Low</Badge>;
}

function scoreColor(score) {
  if (score >= 85) return "text-green-600";
  if (score >= 60) return "text-amber-600";
  return "text-red-600";
}

function IssueRow({ issue }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 py-2">
      <div className="flex items-center gap-2 min-w-0">
        {issue.count > 0 && <AlertTriangle className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
        <span className="text-sm text-foreground/90 truncate">{issue.label}</span>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-sm font-medium">{fmtNumber(issue.count)}</span>
        <span className="text-xs text-muted-foreground w-14 text-right">{issue.pct}% of {fmtNumber(issue.total)}</span>
        {sevBadge(issue.severity)}
      </div>
    </div>
  );
}

export default function DataHealth({ onRegisterExport }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const printRef = useRef(null);

  const { exportPdf, printNode } = useNodeExport(
    printRef,
    () => !!data,
    "salestap-data-health"
  );

  useEffect(() => {
    if (!onRegisterExport) return;
    onRegisterExport("data_health", { exportPdf, printFn: printNode });
  }, [onRegisterExport, exportPdf, printNode, data]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [h, rep] = await Promise.all([
        api.getDataHealth(),
        api.getDataHealthReport().catch(() => ({ report_md: null })),
      ]);
      setData(h);
      setReport(rep?.report_md || null);
    } catch (e) {
      logError("Failed to load data health:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setReportLoading(true);
    try {
      await api.generateDataHealthReport();
      const t0 = Date.now();
      const poll = async () => {
        const st = await api.dataHealthReportStatus();
        if (st.status === "done") {
          setReport(st.report_md);
          setReportLoading(false);
          toast.success("Data-health analysis complete.");
          return;
        }
        if (st.status === "error") {
          setReportLoading(false);
          toast.error(st.error || "Analysis failed.");
          return;
        }
        if (Date.now() - t0 > 180000) {
          setReportLoading(false);
          toast.error("Analysis timed out.");
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setReportLoading(false);
      toast.error(e.response?.data?.detail || "Analysis failed.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError) {
    return <TabLoadError onRetry={load} />;
  }

  const score = data?.score ?? 100;
  const dealIssues = data?.deal_issues || [];
  const contactIssues = data?.contact_issues || [];
  const subtitle = `Health score ${score}/100 · ${fmtNumber(data?.deal_count || 0)} deals · ${fmtNumber(data?.contact_count || 0)} contacts`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-semibold text-foreground leading-tight">Data Health</h2>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <Button data-testid={TID.genDataHealthReportBtn} onClick={generate} disabled={reportLoading} variant="outline" size="sm">
          {reportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {report ? "Regenerate" : "AI Hygiene Review"}
        </Button>
      </div>

      {/* Score */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className={`text-5xl font-bold ${scoreColor(score)}`} data-testid={TID.dataHealthScore}>{score}</p>
              <p className="text-xs text-muted-foreground mt-1">out of 100</p>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-2">CRM Data Health Score</p>
              <Progress value={score} className="h-3" />
              <p className="text-xs text-muted-foreground mt-2">
                Weighted-clean percentage across {dealIssues.length + contactIssues.length} hygiene checks (missing fields, unassigned, overdue, stale).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Deal Issues</CardTitle>
          </CardHeader>
          <CardContent>
            {dealIssues.length ? dealIssues.map((i) => <IssueRow key={i.key} issue={i} />) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No deal data.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact Issues</CardTitle>
          </CardHeader>
          <CardContent>
            {contactIssues.length ? contactIssues.map((i) => <IssueRow key={i.key} issue={i} />) : (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No contact data. (Capture contacts via the Connections tab on a live HubSpot connection.)
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {report && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> AI Data-Hygiene Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <article data-testid={TID.dataHealthReportContent} className="prose prose-slate dark:prose-invert max-w-none prose-h2:text-lg prose-strong:text-foreground">
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{report}</ReactMarkdown>
            </article>
          </CardContent>
        </Card>
      )}

      <TabReportPrint ref={printRef} title="Data Health" subtitle={subtitle} reportMd={report}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #0f172a" }}>
              <th style={{ textAlign: "left", padding: 4 }}>Issue</th>
              <th style={{ textAlign: "right", padding: 4 }}>Count</th>
              <th style={{ textAlign: "right", padding: 4 }}>%</th>
              <th style={{ textAlign: "left", padding: 4 }}>Severity</th>
            </tr>
          </thead>
          <tbody>
            {[...dealIssues, ...contactIssues].map((i) => (
              <tr key={i.key} style={{ borderBottom: "1px solid #e2e8f0" }}>
                <td style={{ padding: 4 }}>{i.label}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{i.count}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{i.pct}%</td>
                <td style={{ padding: 4, textTransform: "capitalize" }}>{i.severity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabReportPrint>
    </div>
  );
}
